/**
 * This module contains the [[Repeat]] helper component used to repeat HTML per collection item.
 */

/***/

import Model, { isAttributeChangeObservable } from '../core/model/Model';
import ListRenderer from '../core/list/ListRenderer';
import View from '../core/view/View';
import { node } from '../core/node/Node';


/**
 * A helper view that repeats its inner HTML once for each item of a given collection.
 * Set its `items` attribute to the collection, and specify the iteration variable's name in the `as` attribute.
 * Within the repeat block, you can access the context of the outside component with `parent`.
 *
 *  ### Basic usage:
 *
 * ```html
 * <div view="repeat" items="[[ items ]]" as="item">
 *   <button on-click="[[ parent.onButton( item ) ]]">[[ item.name ]]</button>
 * </div>
 * ```
 *
 * For this to work, you must have previously registered the component under the key `repeat`.
 * For example:
 *
 * ```typescript
 * import Repeat from '.../components/Repeat';
 * ...
 * scan(document.body, { 'repeat': Repeat }, ...otherViewRegistrations);
 * ```
 */
export default class Repeat extends View<RepeatModel> {

	itemTemplate:string;
	renderer:ListRenderer<any>;

	constructor() {
		super( { template:'', data:new RepeatModel( { as:'', items:[] } ) } );
	}

	prepareRender( target:Element ) {

		// before render get inner html for item template.
		this.itemTemplate = target.innerHTML;
		target.innerHTML = '';
	}

	init() {
		this.renderItems();
		this.data.change.filter( c => c.name == 'items' ).subscribe( this.renderItems.bind( this ) );
	}

	renderItems() {

		node( this.node ).empty();


		// check for valid array
		let items = this.data.items;
		if( !Array.isArray( items ) ) return;

		this.renderer = new ListRenderer( this.node );
		this.renderer.defaultRender( ( item, index ) => {
			let data = {
				parent:this.parent.data,
				index:index,
			};

			// inject item
			data[this.data.as] = item;

			let temp = new View( { template:this.itemTemplate, data:data } );
			temp.parent = this;
			temp.render();

			// setup proper updates with prefix name
			if( isAttributeChangeObservable( item ) ) {
				item.change.subscribe( ( change ) => {
					temp.template.update( data, this.data.as + '.' + change.name );
				});
			}

			return { node: temp.node };
		} );

		this.renderer.source.fill( items );
	}

}


/** @ignore */
class RepeatModel extends Model {
	as:string;
	items:[];
}
